import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Breadcrumbs,
  Divider,
  Card,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, Link } from 'react-router-dom';

import InputField from 'components/formFields/InputField';
import AutocompleteField from 'components/formFields/autocompleteField';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { getApplicationCustomerStore } from 'store/customer';
import { getApplicationDataDropDownStore, saveAppReleaseStore } from 'store/app-releases';

import { appReleaseFormModel } from './formik/create/formModels/appReleaseFormModel';
import { ReleaseType } from 'commons/interfaces';
import formInitialValues from './formik/create/formModels/formInitialValues';
import { validationSchema } from './formik/create/formModels/validationSchema';
import { FeaturesFieldArray } from './components/FeaturesFieldArray';
import { Save, InfoOutlined } from '@mui/icons-material';
import { novedadesReleaseMapper } from './mapper/novedades.maper';
import { InfoModal } from 'components/generics/InfoModal';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 50,
  padding: 24,
  borderRadius: 16,
  boxShadow:
    '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
  border: '1px solid rgba(145, 158, 171, 0.16)',
}));

const calculateNextVersion = (prevVersion: string, type: ReleaseType | string) => {
  if (!prevVersion) return '1.0.0';
  const parts = prevVersion.replace('v', '').split('.').map(Number);
  if (parts.length !== 3) return prevVersion;

  let [major, minor, patch] = parts;
  if (type === ReleaseType.MAJOR) {
    major++;
    minor = 0;
    patch = 0;
  } else if (type === ReleaseType.MINOR) {
    minor++;
    patch = 0;
  } else if (type === ReleaseType.PATCH) {
    patch++;
  }
  return `${major}.${minor}.${patch}`;
};

const VersionHelper = ({ values, setFieldValue }: { values: any; setFieldValue: any }) => {
  const [infoModal, setInfoModal] = useState({ open: false, title: '', desc: '', infoText: '' });

  const updateType = values[appReleaseFormModel.formField.type.name];

  useEffect(() => {
    if (values.application?.version && updateType) {
      const newVer = calculateNextVersion(values.application.version, updateType as any);
      setFieldValue(appReleaseFormModel.formField.version.name, newVer);
    }
  }, [values.application, updateType, setFieldValue]);
  const handleOpenInfo = (e: React.MouseEvent, type: string) => {
    e.stopPropagation();
    e.preventDefault();

    if (type === ReleaseType.PATCH) {
      setInfoModal({
        open: true,
        title: '🐞 Corrección de errores (PATCH)',
        desc: 'Utiliza esta opción cuando solo corriges errores o realizas pequeñas mejoras internas que no agregan funcionalidades nuevas ni afectan la compatibilidad con versiones anteriores.',
        infoText: 'Ejemplo: v1.0.0 → v1.0.1',
      });
    } else if (type === ReleaseType.MINOR) {
      setInfoModal({
        open: true,
        title: '✨ Nueva funcionalidad (MINOR)',
        desc: 'Utiliza esta opción cuando incorporas nuevas funcionalidades o mejoras importantes que son compatibles con las versiones anteriores y no requieren cambios por parte del usuario.',
        infoText: 'Ejemplo: v1.0.0 → v1.1.0',
      });
    } else if (type === ReleaseType.MAJOR) {
      setInfoModal({
        open: true,
        title: '⚠️ Cambio importante (MAJOR)',
        desc: 'Utiliza esta opción cuando introduces cambios incompatibles con versiones anteriores, como modificaciones en la arquitectura, eliminación de funcionalidades o cambios que requieren adaptación por parte del usuario.',
        infoText: 'Ejemplo: v1.0.0 → v2.0.0',
      });
    }
  };

  if (!values.application) return null;

  return (
    <>
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f4f6fc', borderRadius: 2 }}>
        <Typography variant="body2" fontWeight="bold" color="primary" mb={1}>
          Versión actual: v{values.application.version || '1.0.0'}
        </Typography>

        <FormControl>
          <FormLabel sx={{ fontSize: '0.85rem' }}>Selecciona el tipo de actualización:</FormLabel>

          <RadioGroup
            row
            value={updateType || ''}
            onChange={(e) => setFieldValue(appReleaseFormModel.formField.type.name, e.target.value)}
          >
            <FormControlLabel
              value={ReleaseType.PATCH}
              control={<Radio size="small" />}
              label={
                <Box display="flex" alignItems="center">
                  🐞 Corrección de errores (PATCH)
                  <IconButton
                    size="small"
                    sx={{ ml: 0.5 }}
                    onClick={(e) => handleOpenInfo(e, ReleaseType.PATCH)}
                  >
                    <InfoOutlined fontSize="small" color="info" />
                  </IconButton>
                </Box>
              }
            />

            <FormControlLabel
              value={ReleaseType.MINOR}
              control={<Radio size="small" />}
              label={
                <Box display="flex" alignItems="center">
                  ✨ Nueva funcionalidad (MINOR)
                  <IconButton
                    size="small"
                    sx={{ ml: 0.5 }}
                    onClick={(e) => handleOpenInfo(e, ReleaseType.MINOR)}
                  >
                    <InfoOutlined fontSize="small" color="info" />
                  </IconButton>
                </Box>
              }
            />

            <FormControlLabel
              value={ReleaseType.MAJOR}
              control={<Radio size="small" />}
              label={
                <Box display="flex" alignItems="center">
                  ⚠️ Cambio importante (MAJOR)
                  <IconButton
                    size="small"
                    sx={{ ml: 0.5 }}
                    onClick={(e) => handleOpenInfo(e, ReleaseType.MAJOR)}
                  >
                    <InfoOutlined fontSize="small" color="info" />
                  </IconButton>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      </Box>

      <InfoModal
        open={infoModal.open}
        setOpen={(val: boolean) => setInfoModal({ ...infoModal, open: val })}
        title={infoModal.title}
        description={infoModal.desc}
        infoText={infoModal.infoText}
        icon="Info"
      />
    </>
  );
};

export const CreateNovedadesRelease = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { applications_for_select } = useSelector((state: any) => state.appReleases);
  const { handleResponse, ResponseComponents } = useResponseHandler() as any;
  const [loadingApps, setLoadingApps] = useState(false);
  const [appsData, setAppsData] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    getApplicationDataDropDown();
  }, []);

  const getApplicationDataDropDown = async () => {
    const response = await dispatch(getApplicationDataDropDownStore());
    await handleResponse(response, '', false);
  };

  const handleSubmit = async (values: typeof formInitialValues, actions: any) => {
    // aqui
    const mappedValues = novedadesReleaseMapper(values);
    const response = await dispatch(saveAppReleaseStore(mappedValues));
    const success = await handleResponse(response, 'crear novedad', true);
    if (success) {
      setTimeout(() => {
        navigate('/manager/novedades');
      }, 1500);
    }
    actions.setSubmitting(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>
        Crear Novedad (Release)
      </Typography>
      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
        <Typography color="textPrimary" sx={{ fontWeight: 700 }}>
          Manager
        </Typography>
        <Link style={{ color: 'inherit', textDecoration: 'none' }} to="/manager/novedades">
          Listar Novedades
        </Link>
        <Typography color="textPrimary">Crear Novedad</Typography>
      </Breadcrumbs>
      <Divider sx={{ marginBottom: 3 }} />

      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <StyledCard>
            <Form id={appReleaseFormModel.formId} autoComplete="nope">
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
                  Información General
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AutocompleteField
                      name={appReleaseFormModel.formField.application.name}
                      label={appReleaseFormModel.formField.application.label}
                      options={applications_for_select}
                      fullWidth
                    />
                    {loadingApps && <CircularProgress size={20} sx={{ mt: 1 }} />}
                    <VersionHelper values={values} setFieldValue={setFieldValue} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InputField
                      name={appReleaseFormModel.formField.version.name}
                      label={appReleaseFormModel.formField.version.label}
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 2 }}>
                <FeaturesFieldArray />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/manager/novedades')}
                  disabled={isSubmitting}
                >
                  Atras
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  startIcon={<Save />}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Guardando...' : 'Guardar Novedad'}
                </Button>
              </Box>
            </Form>
          </StyledCard>
        )}
      </Formik>
    </Box>
  );
};
